import { useCallback, useEffect, useRef } from 'react';
import { Row, Col, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { SelectGeneric, Label } from 'ypf-pas-general-ui-components';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { BsFillExclamationCircleFill } from 'react-icons/bs';

import {
  getBudgetService,
  getImputationDefault,
  getImputationsDetails,
  getImputationsObjects,
  getLogisticCenters,
  getSocieties
} from '../../../services/yclickMaster';
import { imputationTypeEnum } from '../../../utils/dataStatus';
import useToast from '../../../hooks/useToast';
import { mapperImputationDefault } from '../../../utils/mapper';

const schemaValidation = yup.object().shape({
  imputationType: yup.object().required('Seleccione un tipo de imputación').nullable(),
  imputationObject: yup.object().required('Seleccione un objeto de imputación').nullable()
});

const AccountAssignmentObjectForm = ({
  handleChangeForm,
  imputationTypeOptions,
  disabledForm,
  // imputationObjectDefault,
  wellId,
  setLoading,
  data,
  eventPozoId,
  form,
  index,
  fromPteAuthorization
}) => {
  const initialValues = {
    imputationType: data?.imputationType ? data.imputationType : undefined,
    imputationObject: data?.imputationObject ? data?.imputationObject : undefined,
    state: data.state,
    budget: data.budget,
    logisticCenter: data.logisticCenter,
    socCode: data.socCode,
    soc: data.soc,
    description: data.description,
    warningBudget: data.warningBudget,
    warningState: false,
    logisticCenterOptions: [],
    societiesOptions: []
  };

  const { setNewToast } = useToast();
  const isPep = data?.imputationType?.id === imputationTypeEnum.PEP;
  const showAlert = data?.alerta || (data?.warningState && isPep);

  const formRef = useRef();
  const searchImputationObject = async (typeId, objectName) => {
    if (objectName.length < 5) return null;
    try {
      const objectResponse = await getImputationsObjects(typeId, objectName);

      return objectResponse.map((res) => ({
        id: res.id,
        nombre: res.codigo && res.descripcion ? `${res.codigo} | ${res.descripcion}` : undefined
      }));
    } catch (err) {
      return null;
    }
  };

  const getLogisticCenterOptions = async (societyCode) => {
    try {
      const societyParam = data?.socCode ?? societyCode?.nombre;
      const response = await getLogisticCenters(isPep, societyParam ?? '');
      const logisticCenters = response.map((res) => ({
        id: res.id,
        nombre: res.nombre
      }));

      return formRef.current.setFieldValue('logisticCenterOptions', logisticCenters);
    } catch (err) {
      return null;
    }
  };

  const getSocietiesOptions = async (logCenter) => {
    try {
      const logisticCenterParam = data?.logisticCenter ?? logCenter?.nombre;
      const response = await getSocieties(isPep, logisticCenterParam ?? '');
      const societiesOptions = response.map((res) => ({
        id: res.id,
        nombre: res?.nombre
      }));

      return formRef.current.setFieldValue('societiesOptions', societiesOptions);
    } catch (err) {
      return null;
    }
  };

  const getImputationDetail = async (typeId, objectId) => {
    setLoading(true);
    try {
      const responseDetail = await getImputationsDetails([
        {
          pozoId: wellId,
          tipoId: typeId,
          objetoId: objectId
        }
      ]);

      const [item] = responseDetail;
      const response = mapperImputationDefault(item, { id: typeId });

      await formRef.current.setFieldValue('state', response.state);
      await formRef.current.setFieldValue('logisticCenter', response.logisticCenter);
      await formRef.current.setFieldValue('socCode', response.socCode);
      await formRef.current.setFieldValue('soc', response.soc);
      await formRef.current.setFieldValue('description', response.description);
      await formRef.current.setFieldValue('warningState', response.warningState);

      // Obtener presupuesto solo si TI es PEP
      if (typeId === imputationTypeEnum.PEP) {
        const budget = await getBudgetService(objectId);

        await formRef.current.setFieldValue('budget', budget.presupuesto);
        await formRef.current.setFieldValue('warningBudget', budget.presupuesto < 10000);
      }

      handleChangeForm(formRef.current.values);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const clearDetails = async () => {
    await formRef.current.setFieldValue('state', '-');
    await formRef.current.setFieldValue('budget', null);
    await formRef.current.setFieldValue('logisticCenter', null);
    await formRef.current.setFieldValue('socCode', null);
    await formRef.current.setFieldValue('soc', null);
    await formRef.current.setFieldValue('description', '-');
    await formRef.current.setFieldValue('warningBudget', false);
    await formRef.current.setFieldValue('warningState', false);
    await formRef.current.setFieldValue('logisticCenterOptions', []);
    await formRef.current.setFieldValue('societiesOptions', []);

    handleChangeForm(formRef.current.values);
  };

  const fetchImputationDefault = useCallback(async (eventoPozoId) => {
    try {
      const imputationResponse = await getImputationDefault([{ eventoPozoId }]);

      const imputationType = imputationResponse[0]?.tipoImputacion;
      const imputationObject = imputationResponse[0]?.objetoImputacion;

      // if (!imputationType && !imputationObject) return;
      formRef.current.setFieldValue('imputationType', {
        ...imputationType,
        label: imputationType?.nombre ?? null,
        value: imputationType?.id ?? null
      });
      formRef.current.setFieldValue('imputationObject', {
        ...imputationObject,
        label:
          imputationObject?.codigo && imputationObject?.descripcion
            ? `${imputationObject?.codigo} | ${imputationObject?.descripcion}`
            : null,
        nombre:
          imputationObject?.codigo && imputationObject?.descripcion
            ? `${imputationObject?.codigo} | ${imputationObject?.descripcion}`
            : null,
        value: imputationObject?.id
      });
      if (imputationType?.id && imputationObject?.id) {
        getImputationDetail(imputationType?.id, imputationObject?.id);
      }
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTooltip = (propsTooltip) => (
    <Tooltip id="button-tooltip" {...propsTooltip}>
      {data.description}
    </Tooltip>
  );

  const showSociety = () => {
    const society = data.soc;

    if (Array.isArray(society)) {
      return society.map((soc) => (
        <img alt="society-img" className="society-img" key={soc} src={soc} />
      ));
    }

    return '-';
  };

  useEffect(() => {
    // Este caso se dará solo para ASDS con rol Prov. Notif (Por Ahora)
    if (eventPozoId) {
      fetchImputationDefault(eventPozoId);
    }
  }, [eventPozoId, fetchImputationDefault]);

  useEffect(() => {
    if (fromPteAuthorization) {
      getLogisticCenterOptions();
      getSocietiesOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Formik initialValues={initialValues} innerRef={formRef} validationSchema={schemaValidation}>
      {({ values, setFieldValue, setFieldTouched }) => (
        <Form>
          {showAlert && fromPteAuthorization && (
            <div className="container-alert-well">
              <p>El objeto de imputacion presenta inconvenientes, se envia como excepcion YCLICK</p>
            </div>
          )}
          <Row>
            <Col sm={3}>
              <SelectGeneric
                isDisabled={disabledForm}
                label="Tipo de imputación *"
                name="imputationType"
                onChange={(newValue) => {
                  setFieldValue('imputationType', newValue);
                  setFieldValue('imputationObject', null);
                  setFieldTouched('imputationType');
                  clearDetails();
                }}
                options={imputationTypeOptions}
                paddingIndicator={4}
                placeholder="Tipo imputación"
                value={values.imputationType}
                withSelect="100%"
              />
            </Col>
            <Col sm={9}>
              <SelectGeneric
                async
                fetchData={(value) => searchImputationObject(values.imputationType.id, value)}
                isDisabled={!values.imputationType || disabledForm}
                label="Objeto de imputación | Denominación objeto *"
                name="imputationObject"
                onChange={(newValue) => {
                  setFieldValue('imputationObject', newValue);
                  setFieldTouched('imputationObject');
                  if (newValue) {
                    getImputationDetail(values.imputationType.value, newValue.value);
                  } else {
                    clearDetails();
                  }
                }}
                paddingIndicator={4}
                placeholder="Buscar por nombre"
                value={values.imputationObject}
                withSelect="100%"
              />
            </Col>
            {fromPteAuthorization && (
              <Col sm={2}>
                <OverlayTrigger
                  delay={{
                    show: 250,
                    hide: 400
                  }}
                  overlay={renderTooltip}
                  placement="top"
                >
                  <div>
                    <Label
                      icon={data.warningState ? BsFillExclamationCircleFill : null}
                      iconPosition="left"
                      iconWarning={data.warningState}
                      label="Estado"
                      size="md"
                      sizeValue="md"
                      value={data.state}
                      variant="light"
                    />
                  </div>
                </OverlayTrigger>
                {data?.soc && (
                  <Label
                    label="Sociedad"
                    size="md"
                    sizeValue="md"
                    value={<div className="detail society">{showSociety()}</div>}
                    variant="light"
                  />
                )}
              </Col>
            )}
            {fromPteAuthorization && (
              <>
                <Col sm={1} />
                <Col sm={3}>
                  <Label
                    icon={data.warningBudget ? BsFillExclamationCircleFill : null}
                    iconPosition="left"
                    iconWarning={data.warningBudget}
                    label="Presupuesto disponible"
                    size="md"
                    sizeValue="md"
                    value={`${data.budget ? `U$D ${data.budget}` : '-'}`}
                    variant="light"
                  />
                </Col>
                <>
                  {data.logisticCenter ? (
                    <Col sm={3}>
                      <Label
                        label="Centro logístico"
                        size="md"
                        sizeValue="md"
                        value={data?.logisticCenter || '-'}
                        variant="light"
                      />
                    </Col>
                  ) : (
                    <Col sm={3}>
                      <SelectGeneric
                        label="Centro Logístico"
                        name="logisticCenter"
                        onChange={(newValue) => {
                          const logisticCenterModified = `imputationObject.wells[${index}].logisticCenter`;

                          getSocietiesOptions(newValue);
                          setFieldValue('logisticCenter', newValue);
                          form?.current?.setFieldValue(logisticCenterModified, newValue.nombre);
                          setFieldValue('socCode', null);
                        }}
                        optionMapper={{
                          value: 'id',
                          label: 'nombre'
                        }}
                        options={values?.logisticCenterOptions}
                        paddingIndicator={4}
                        placeholder="Seleccionar centro logistico"
                        value={values?.logisticCenter}
                        withSelect="100%"
                      />
                    </Col>
                  )}
                </>
                <>
                  {data.socCode ? (
                    <Col sm={3}>
                      <Label
                        label="Código de sociedad"
                        size="md"
                        sizeValue="md"
                        value={data.socCode || '-'}
                        variant="light"
                      />
                    </Col>
                  ) : (
                    <Col sm={3}>
                      <SelectGeneric
                        label="Código de Sociedad"
                        name="socCode"
                        onChange={(newValue) => {
                          const socCodeModified = `imputationObject.wells[${index}].socCode`;

                          setFieldValue('socCode', newValue);
                          form?.current?.setFieldValue(socCodeModified, newValue.nombre);
                        }}
                        optionMapper={{
                          value: 'id',
                          label: 'nombre'
                        }}
                        options={values?.societiesOptions}
                        paddingIndicator={4}
                        placeholder="Seleccionar sociedad"
                        value={values?.socCode}
                        withSelect="100%"
                      />
                    </Col>
                  )}
                </>
              </>
            )}
          </Row>
        </Form>
      )}
    </Formik>
  );
};

AccountAssignmentObjectForm.propTypes = {
  handleChangeForm: PropTypes.func,
  setLoading: PropTypes.func,
  imputationTypeOptions: PropTypes.arrayOf(PropTypes.shape()),
  disabledForm: PropTypes.bool,
  // disabledDelete: PropTypes.bool,
  wellId: PropTypes.number,
  data: PropTypes.shape({
    imputationType: PropTypes.shape({
      label: PropTypes.string,
      id: PropTypes.number
    }),
    imputationObject: PropTypes.shape({
      label: PropTypes.string,
      id: PropTypes.number
    }),
    state: PropTypes.string,
    budget: PropTypes.number,
    logisticCenter: PropTypes.string,
    socCode: PropTypes.string,
    soc: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    warningBudget: PropTypes.bool,
    warningState: PropTypes.bool,
    alerta: PropTypes.bool
  }),
  imputationObjectDefault: PropTypes.bool,
  eventPozoId: PropTypes.number,
  form: PropTypes.objectOf(PropTypes.shape),
  index: PropTypes.number.isRequired,
  fromPteAuthorization: PropTypes.bool
};

AccountAssignmentObjectForm.defaultProps = {
  handleChangeForm: () => {},
  setLoading: () => {},
  imputationTypeOptions: [],
  disabledForm: false,
  wellId: 0,
  data: {
    imputationType: null,
    imputationObject: null,
    state: null,
    budget: null,
    logisticCenter: null,
    socCode: null,
    soc: null,
    description: null,
    warningBudget: false,
    warningState: false
  },
  imputationObjectDefault: false,
  eventPozoId: 0,
  form: {},
  fromPteAuthorization: false
};

export default AccountAssignmentObjectForm;
